import { ApiError } from "@nabble/sdk";
import { useToast } from "@nabble/ui";
import { type FormEvent, useState } from "react";
import { changePassword } from "../../../../api/accountPassword";
import { passwordChecks, type PolicyCheck } from "../../../../components/PasswordPolicy";
import { errorMessage } from "../../../../errors";

export interface ChangePasswordForm {
  current: string;
  setCurrent: (value: string) => void;
  next: string;
  setNext: (value: string) => void;
  confirm: string;
  setConfirm: (value: string) => void;
  checks: PolicyCheck[];
  valid: boolean;
  loading: boolean;
  currentError: string | null;
  error: string | null;
  violations: string[];
  submit: (e: FormEvent) => Promise<void>;
}

/** The change-password form: its fields, the local checks and the server's answer, each error by its field. */
export function useChangePassword(onChanged: () => void): ChangePasswordForm {
  const { show } = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [violations, setViolations] = useState<string[]>([]);
  const checks = passwordChecks(next, confirm);
  const valid = current.length > 0 && checks.every((c) => c.ok);

  function showFailure(err: unknown): void {
    if (err instanceof ApiError && err.code === "weak_password" && err.body.violations) setViolations(err.body.violations);
    else if (err instanceof ApiError && err.code === "invalid_credentials") setCurrentError("Your current password isn't correct.");
    else setError(errorMessage(err, "We couldn't change the password."));
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!valid) return;
    setLoading(true);
    setError(null);
    setCurrentError(null);
    setViolations([]);
    try {
      const result = await changePassword(current, next);
      setCurrent("");
      setNext("");
      setConfirm("");
      show({
        title: "Password updated",
        text: result.otherSessionsEnded ? "We signed out your other open sessions." : undefined,
        tone: "success",
      });
      onChanged();
    } catch (err) {
      showFailure(err);
    } finally {
      setLoading(false);
    }
  }

  return { current, setCurrent, next, setNext, confirm, setConfirm, checks, valid, loading, currentError, error, violations, submit };
}
